import { memo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  FileCode,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { getReadableErrorMessage } from "@/shared/lib/sefaz-message";
import {
  downloadBlob,
  openBlankTab,
  openBlobInTab,
} from "@/shared/lib/download";
import { formatCurrency } from "@/shared/lib/formatters";
import { commercialInvoiceService } from "../services/commercial-invoice.service";
import { danfeFilename } from "../utils/danfe";
import type { CommercialInvoiceResponseDTO } from "../types/commercial-invoice.types";

const COPY_FEEDBACK_MS = 2000;

interface CommercialInvoiceEmitResultProps {
  invoice: CommercialInvoiceResponseDTO;
}

export const CommercialInvoiceEmitResult = memo(
  function CommercialInvoiceEmitResult({
    invoice,
  }: CommercialInvoiceEmitResultProps) {
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);

    const isPending = invoice.status === "PENDING";
    const isAuthorized = invoice.status === "ISSUED";
    const reference = invoice.ref ?? String(invoice.id);

    const handleCopyKey = async () => {
      if (!invoice.nfeKey) return;
      try {
        await navigator.clipboard.writeText(invoice.nfeKey);
        setCopied(true);
        window.setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
      } catch {
        toast.error("Não foi possível copiar. Selecione a chave manualmente.");
      }
    };

    const handleOpenDanfe = async () => {
      const tabOpenedWithinUserGesture = openBlankTab();
      setBusy(true);
      try {
        const blob = await commercialInvoiceService.downloadPdf(invoice.id);
        if (!openBlobInTab(blob, tabOpenedWithinUserGesture)) {
          downloadBlob(blob, danfeFilename(blob, reference));
        }
      } catch (error: unknown) {
        tabOpenedWithinUserGesture?.close();
        toast.error(
          getReadableErrorMessage(
            error,
            "Não foi possível abrir o DANFE. Tente novamente.",
          ),
        );
      } finally {
        setBusy(false);
      }
    };

    const handleDownloadXml = async () => {
      setBusy(true);
      try {
        const blob = await commercialInvoiceService.downloadXml(invoice.id);
        downloadBlob(blob, `nfce-${reference}.xml`);
      } catch (error: unknown) {
        toast.error(
          getReadableErrorMessage(
            error,
            "Não foi possível baixar o XML. Tente novamente.",
          ),
        );
      } finally {
        setBusy(false);
      }
    };

    return (
      <div className="space-y-4">
        <div
          className={
            isPending
              ? "rounded-md border border-amber-500/20 bg-amber-500/10 p-4"
              : "rounded-md border border-emerald-500/20 bg-emerald-500/10 p-4"
          }
        >
          <div className="flex items-start gap-3">
            {isPending ? (
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            ) : (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            )}
            <div className="space-y-1">
              <p className="font-semibold">
                {isPending
                  ? "Emissão sem confirmação"
                  : "Nota autorizada pela SEFAZ"}
              </p>
              <p className="text-sm text-muted-foreground">
                {isPending
                  ? "A nota foi registrada, mas a confirmação não chegou a tempo. Ela pode ter sido autorizada — use Reconsultar na listagem antes de emitir outra."
                  : `Nota de ${formatCurrency(invoice.value)} emitida com sucesso.`}
              </p>
            </div>
          </div>
        </div>

        <dl className="rounded-md border divide-y text-sm">
          <div className="flex flex-col gap-1 p-3">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              Número da nota
            </dt>
            <dd className="font-medium">
              {invoice.invoiceNumber
                ? `${invoice.invoiceNumber}${
                    invoice.invoiceSeries
                      ? ` · série ${invoice.invoiceSeries}`
                      : ""
                  }`
                : "Ainda não atribuído"}
            </dd>
          </div>

          <div className="flex flex-col gap-1 p-3">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              Chave de acesso
            </dt>
            <dd className="flex items-center justify-between gap-2">
              <span className="font-mono text-xs break-all leading-relaxed">
                {invoice.nfeKey ?? "Ainda não disponível"}
              </span>
              {invoice.nfeKey && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={handleCopyKey}
                  title="Copiar chave de acesso"
                  aria-label="Copiar chave de acesso"
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              )}
            </dd>
          </div>

          {invoice.email && (
            <div className="flex flex-col gap-1 p-3">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                Enviada para
              </dt>
              <dd className="font-medium break-all">{invoice.email}</dd>
            </div>
          )}
        </dl>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            type="button"
            onClick={handleOpenDanfe}
            disabled={!isAuthorized || busy}
            title={
              isAuthorized ? undefined : "O DANFE só existe após a autorização"
            }
            className="gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Abrir DANFE
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleDownloadXml}
            disabled={!isAuthorized || busy}
            className="gap-2"
          >
            <FileCode className="w-4 h-4" />
            Baixar XML
          </Button>

          {invoice.consultUrl && (
            <Button asChild type="button" variant="outline" className="gap-2">
              <a
                href={invoice.consultUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="w-4 h-4" />
                Consultar na SEFAZ
              </a>
            </Button>
          )}
        </div>
      </div>
    );
  },
);
